import { useService } from "./service.js";
import useRaf from "./raf.js";
import inertiaFinalValue from "../utils/math/inertiaFinalValue.js";
import { isDefined } from "../utils/index.js";
const MODES = {
  START: "start",
  DRAG: "drag",
  DROP: "drop",
  INERTIA: "inertia",
  STOP: "stop"
};
let count = 0;
const targetEvents = ["pointerdown"];
const windowEvents = ["pointerup", "touchend"];
const passiveEventOptions = { passive: true };
function getEventPosition(event) {
  const eventOrTouch = isDefined(event.touches) ? event.touches[0] : event;
  return {
    x: eventOrTouch.clientX,
    y: eventOrTouch.clientY
  };
}
function createDragService(target, { dampFactor = 0.85, dragTreshold = 10 } = {}) {
  count += 1;
  const id = `drag-${count}`;
  let previousEvent;
  function shouldPreventClick() {
    return Math.abs(props.distance.x) > dragTreshold || Math.abs(props.distance.y) > dragTreshold;
  }
  function start(x, y) {
    if (props.isGrabbing) {
      return;
    }
    props.x = x;
    props.y = y;
    props.origin.x = x;
    props.origin.y = y;
    props.delta.x = 0;
    props.delta.y = 0;
    props.distance.x = 0;
    props.distance.y = 0;
    props.final.x = x;
    props.final.y = y;
    props.mode = MODES.START;
    props.isGrabbing = true;
    trigger(props);
    document.addEventListener("touchmove", handleEvent, passiveEventOptions);
    document.addEventListener("mousemove", handleEvent, passiveEventOptions);
  }
  function drop() {
    document.removeEventListener("touchmove", handleEvent);
    document.removeEventListener("mousemove", handleEvent);
    props.isGrabbing = false;
    props.mode = MODES.DROP;
    props.hasInertia = true;
    props.final.x = inertiaFinalValue(props.x, props.delta.x, dampFactor);
    props.final.y = inertiaFinalValue(props.y, props.delta.y, dampFactor);
    trigger(props);
    setTimeout(() => {
      const raf = useRaf();
      raf.remove(id);
      raf.add(id, rafHandler);
    }, 0);
  }
  function stop() {
    useRaf().remove(id);
    props.isGrabbing = false;
    props.hasInertia = false;
    props.mode = MODES.STOP;
    trigger(props);
  }
  function rafHandler() {
    if (!props.isGrabbing) {
      props.x += props.delta.x;
      props.y += props.delta.y;
      props.distance.x = props.x - props.origin.x;
      props.distance.y = props.y - props.origin.y;
      props.delta.x *= dampFactor;
      props.delta.y *= dampFactor;
      if (props.mode !== MODES.INERTIA) {
        props.mode = MODES.INERTIA;
      }
      trigger(props);
      if (Math.abs(props.delta.x) < 0.1 && Math.abs(props.delta.y) < 0.1) {
        stop();
      }
    }
  }
  function drag(event) {
    if (props.isGrabbing) {
      const position = getEventPosition(event);
      props.x = position.x;
      props.y = position.y;
      if (previousEvent) {
        const previousPosition = getEventPosition(previousEvent);
        props.delta.x = position.x - previousPosition.x;
        props.delta.y = position.y - previousPosition.y;
      }
      props.final.x = position.x;
      props.final.y = position.y;
      props.distance.x = props.x - props.origin.x;
      props.distance.y = props.y - props.origin.y;
      if (props.mode !== MODES.DRAG) {
        props.mode = MODES.DRAG;
      }
      trigger(props);
      previousEvent = event;
    }
  }
  function handleEvent(event) {
    switch (event.type) {
      case "dragstart":
        event.preventDefault();
        break;
      case "click":
        if (shouldPreventClick()) {
          event.stopImmediatePropagation();
          event.stopPropagation();
          event.preventDefault();
        }
        break;
      case "pointerup":
      case "touchend":
        drop();
        break;
      case "touchmove":
      case "mousemove":
        drag(event);
        break;
      default:
        if (event.button === 0) {
          start(event.x, event.y);
        }
    }
  }
  const { add, remove, has, trigger, props } = useService({
    props: {
      target,
      mode: void 0,
      isGrabbing: false,
      hasInertia: false,
      x: 0,
      y: 0,
      delta: {
        x: 0,
        y: 0
      },
      origin: {
        x: 0,
        y: 0
      },
      distance: {
        x: 0,
        y: 0
      },
      final: {
        x: 0,
        y: 0
      }
    },
    init() {
      for (const event of targetEvents) {
        target.addEventListener(event, handleEvent, passiveEventOptions);
      }
      for (const event of windowEvents) {
        window.addEventListener(event, handleEvent, passiveEventOptions);
      }
      target.addEventListener("dragstart", handleEvent, { capture: true });
      target.addEventListener("click", handleEvent, { capture: true });
    },
    kill() {
      for (const event of targetEvents) {
        target.removeEventListener(event, handleEvent);
      }
      for (const event of windowEvents) {
        window.removeEventListener(event, handleEvent);
      }
      target.removeEventListener("dragstart", handleEvent);
      target.removeEventListener("click", handleEvent);
    }
  });
  return {
    add,
    remove,
    has,
    props: () => props
  };
}
const instances = /* @__PURE__ */ new WeakMap();
function useDrag(target, options) {
  if (!instances.has(target)) {
    instances.set(target, createDragService(target, options));
  }
  return instances.get(target);
}
export {
  useDrag as default
};
//# sourceMappingURL=drag.js.map
